import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ItemForm from "./ItemForm";
import { itemService } from "../../services/item.service";

export default function ItemDrawer({ item, onClose, onSaved }) {
  const [open, setOpen] = useState(true);

  const [form, setForm] = useState({
    type: "product", // product | service
    name: "",
    description: "",
    hsn: "",
    sac: "",
    uom: "",
    billingFrequency: null, // Only for services
    price: "",
    tax: "",
    notes: "",
  });

  /* ─────────────────────────────────────
     Close with animation (single source)
  ───────────────────────────────────── */
  function handleClose() {
    setOpen(false);
  }

  /* ESC key (desktop UX) */
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") handleClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ─────────────────────────────────────
     Hydrate form (EDIT vs ADD)
  ───────────────────────────────────── */
  useEffect(() => {
    if (item) {
      // EDIT MODE
      // Map database 'item_type' to form 'type'
      const formType = item.item_type === 'goods' ? 'product' : 
                       item.item_type === 'services' ? 'service' : 
                       item.type || 'product';
      
      setForm({
        type: formType,
        name: item.name || "",
        description: item.description || "",
        hsn: item.hsn || "",
        sac: item.sac || "",
        uom: item.uom || "",
        billingFrequency: item.billingFrequency || null,
        price: item.price ?? item.rate ?? "",
        tax: item.tax ?? item.tax_rate ?? item.gst_rate ?? "",
        notes: item.notes || "",
      });
    } else {
      // ADD MODE
      setForm({
        type: "product",
        name: "",
        description: "",
        hsn: "",
        sac: "",
        uom: "",
        billingFrequency: null,
        price: "",
        tax: "",
        notes: "",
      });
    }
  }, [item]);

  /* ─────────────────────────────────────
     SAVE
  ───────────────────────────────────── */
  async function handleSubmit() {
    if (item?.id) {
      // ✅ EDIT
      await itemService.update(item.id, form);
    } else {
      // ✅ ADD
      await itemService.add(form);
    }

    onSaved?.();   // refresh list
    handleClose(); // animate close
  }

  return (
    <AnimatePresence onExitComplete={onClose}>
      {open && (
        <motion.div
          className="fixed inset-0 z-40 flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {/* Overlay */}
          <motion.div
            className="flex-1 bg-black/30"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: 420 }}
            animate={{ x: 0 }}
            exit={{ x: 420 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 30,
            }}
            className="
              w-[420px]
              h-screen
              bg-white
              border-l border-[var(--color-border)]
              flex flex-col
            "
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[var(--color-border)] shrink-0">
              <h2 className="text-lg font-semibold">
                {item ? "Edit Item" : "Add Item"}
              </h2>
              <p className="text-xs text-[var(--color-text-muted)]">
                Product or service details
              </p>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
              >
                <ItemForm form={form} setForm={setForm} />
              </motion.div>
            </div>

            {/* Footer */}
            <div
              className="
                shrink-0
                px-5 py-4
                border-t border-[var(--color-border)]
                bg-white
                flex justify-end gap-3
              "
            >
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm rounded-md hover:bg-[var(--color-bg-muted)]"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm rounded-md bg-[var(--color-primary)] text-white"
              >
                Save Item
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
